import { useState, useCallback, useRef, useMemo } from 'react';
import { collection, query, where, orderBy, limit, getDocs, startAfter } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from './useAuth';
import { useOfflineAwareData } from './useOfflineAwareData';
import { useModalPerformanceMonitoring } from './usePerformanceMonitoring';

/**
 * Orders Data Management Hook
 * 
 * Provides comprehensive orders data management with:
 * - Pagination and filtering
 * - Offline support
 * - Performance monitoring
 * - Loading states
 * - Error handling
 */

export const useOrdersData = ({
  filters = {},
  pageSize = 20,
  modalId = 'orders-data',
  userScope = 'current_user' // 'all' | 'current_user' | 'other_users'
} = {}) => {
  const { userId, isLoading: authLoading } = useAuth();
  const { trackAsyncOperation } = useModalPerformanceMonitoring(modalId, true);
  const { loadOrdersWithFallback } = useOfflineAwareData();

  // State management
  const [state, setState] = useState({
    orders: [],
    loading: false,
    error: null,
    hasMore: true,
    lastDoc: null,
    totalCount: 0
  });

  const lastDocRef = useRef(null);
  const isLoadingRef = useRef(false);

  // Extract filter values
  const {
    casefileIds = [],
    evidenceIds = [],
    bountyType,
    statuses = [],
    providers = [],
    dateRange
  } = filters;

  // Memoized filter key for cache invalidation
  const filterKey = useMemo(() => 
    JSON.stringify({
      casefileIds: casefileIds.sort(),
      evidenceIds: evidenceIds.sort(),
      bountyType,
      statuses: statuses.sort(),
      providers: providers.sort(),
      dateRange: dateRange ? {
        start: dateRange.start?.toISOString(),
        end: dateRange.end?.toISOString()
      } : null
    }),
    [casefileIds, evidenceIds, bountyType, statuses, providers, dateRange]
  );

  // Create network fetcher function
  const createNetworkFetcher = useCallback((reset = false) => {
    return async () => {
      let qRef = collection(db, 'orders');
      const constraints = [];
      
      // Apply user scope filtering
      if (userScope === 'current_user') {
        constraints.push(where('userId', '==', userId));
      } else if (userScope === 'other_users') {
        constraints.push(where('userId', '!=', userId));
      }
      // For 'all' scope, no user filtering is applied
      
      // Apply evidence filtering
      if (evidenceIds.length > 0) {
        if (evidenceIds.length === 1) {
          constraints.push(where('evidenceIds', 'array-contains', evidenceIds[0]));
        }
        // For multiple evidence IDs, we'll filter client-side
      }
      
      // Apply casefile filtering
      if (casefileIds.length > 0) {
        if (casefileIds.length === 1) {
          constraints.push(where('casefileId', '==', casefileIds[0]));
        } else {
          constraints.push(where('casefileId', 'in', casefileIds.slice(0, 10)));
        }
      }
      
      // Apply bounty type filtering
      if (bountyType) {
        constraints.push(where('orderType', '==', 
          bountyType === 'request' ? 'bounty_request' : 'bounty_fulfillment'
        ));
      }
      
      // Apply status filtering
      if (statuses.length > 0) {
        constraints.push(where('status', 'in', statuses));
      }
      
      // Add ordering and pagination
      constraints.push(orderBy('createdAt', 'desc'));
      constraints.push(limit(pageSize));
      
      if (!reset && lastDocRef.current) {
        constraints.push(startAfter(lastDocRef.current));
      }
      
      qRef = query(qRef, ...constraints);
      const snap = await getDocs(qRef);
      const items = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      
      // Update pagination reference
      if (snap.docs.length > 0) {
        lastDocRef.current = snap.docs[snap.docs.length - 1];
      }
      
      return items;
    };
  }, [userId, evidenceIds, casefileIds, bountyType, statuses, pageSize, userScope]);

  // Client-side filtering for complex queries
  const applyClientSideFilters = useCallback((orders) => {
    let filtered = orders;
    
    // Filter by multiple evidence IDs
    if (evidenceIds.length > 1) {
      filtered = filtered.filter(order => 
        order.evidenceIds && 
        evidenceIds.some(eid => order.evidenceIds.includes(eid))
      );
    }
    
    // Filter by multiple casefile IDs (if not handled server-side)
    if (casefileIds.length > 10) {
      filtered = filtered.filter(order => casefileIds.includes(order.casefileId));
    }
    
    // Filter by providers
    if (providers.length > 0) {
      filtered = filtered.filter(order => 
        order.providers?.some(p => providers.includes(p))
      );
    }
    
    // Filter by date range
    if (dateRange) {
      filtered = filtered.filter(order => {
        const orderDate = order.createdAt?.toDate();
        return orderDate && 
               orderDate >= dateRange.start && 
               orderDate <= dateRange.end;
      });
    }
    
    return filtered;
  }, [evidenceIds, casefileIds, providers, dateRange]);

  // Load orders with pagination
  const loadOrders = useCallback(async (reset = false) => {
    if (isLoadingRef.current || authLoading || !userId) return;
    
    // Debug logging
    if (process.env.NODE_ENV === 'development') {
      console.log('📊 useOrdersData.loadOrders called:', {
        reset,
        userId,
        authLoading,
        filters,
        hasMore: state.hasMore,
        currentOrdersCount: state.orders.length
      });
    }
    
    isLoadingRef.current = true;
    setState(prev => ({ 
      ...prev, 
      loading: true, 
      error: null,
      ...(reset && { orders: [], hasMore: true })
    }));
    
    try {
      const networkFetcher = createNetworkFetcher(reset);
      
      const rawOrders = await trackAsyncOperation('loadOrders',
        loadOrdersWithFallback(networkFetcher, filters, {
          cacheFirst: !reset,
          operationId: `orders_${reset ? 'reset' : 'page'}_${Date.now()}`
        })
      );
      
      // Apply client-side filtering
      const filteredOrders = applyClientSideFilters(rawOrders);
      
      setState(prev => ({
        ...prev,
        orders: reset ? filteredOrders : [...prev.orders, ...filteredOrders],
        hasMore: rawOrders.length === pageSize,
        totalCount: reset ? filteredOrders.length : prev.totalCount + filteredOrders.length,
        loading: false
      }));
      
      return filteredOrders;
      
    } catch (error) {
      console.error('Error loading orders:', error);
      setState(prev => ({
        ...prev,
        loading: false,
        error: error.message || 'Failed to load orders'
      }));
      throw error;
    } finally {
      isLoadingRef.current = false;
    }
  }, [
    authLoading, 
    userId, 
    createNetworkFetcher, 
    trackAsyncOperation, 
    loadOrdersWithFallback, 
    filterKey, // Use filterKey instead of filters to prevent infinite loops
    applyClientSideFilters, 
    pageSize,
    userScope
  ]);

  // Load more orders (pagination)
  const loadMore = useCallback(async () => {
    if (!state.hasMore || state.loading) return;
    return await loadOrders(false);
  }, [state.hasMore, state.loading, loadOrders]);

  // Refresh orders (reset pagination)
  const refresh = useCallback(async () => {
    lastDocRef.current = null;
    return await loadOrders(true);
  }, [loadOrders]);

  // Reset pagination reference when filters change (but don't auto-refresh)
  const resetRef = useRef(filterKey);
  if (resetRef.current !== filterKey) {
    resetRef.current = filterKey;
    lastDocRef.current = null;
    // Note: Auto-refresh removed to prevent infinite loops
    // Parent components should manually call loadOrders(true) when filters change
  }

  // Get order by ID
  const getOrderById = useCallback((orderId) => {
    return state.orders.find(order => order.id === orderId);
  }, [state.orders]);

  // Get orders by casefile
  const getOrdersByCasefile = useCallback((casefileId) => {
    return state.orders.filter(order => order.casefileId === casefileId);
  }, [state.orders]);

  // Get orders by evidence
  const getOrdersByEvidence = useCallback((evidenceId) => {
    return state.orders.filter(order => 
      order.evidenceIds?.includes(evidenceId)
    );
  }, [state.orders]);

  // Statistics
  const stats = useMemo(() => {
    const orders = state.orders;
    return {
      total: orders.length,
      completed: orders.filter(o => o.status === 'completed').length,
      processing: orders.filter(o => o.status === 'processing').length,
      failed: orders.filter(o => o.status === 'failed').length,
      totalCost: orders.reduce((sum, o) => sum + (o.totalCost || 0), 0),
      providers: [...new Set(orders.flatMap(o => o.providers || []))],
      dateRange: orders.length > 0 ? {
        earliest: Math.min(...orders.map(o => o.createdAt?.toDate?.()?.getTime() || 0)),
        latest: Math.max(...orders.map(o => o.createdAt?.toDate?.()?.getTime() || 0))
      } : null
    };
  }, [state.orders]);

  // Note: Auto-load removed to prevent infinite loops
  // Data loading is triggered manually by the consuming component

  return {
    // Data
    orders: state.orders,
    stats,
    
    // State
    loading: state.loading,
    error: state.error,
    hasMore: state.hasMore,
    totalCount: state.totalCount,
    
    // Actions
    loadOrders,
    loadMore,
    refresh,
    
    // Utilities
    getOrderById,
    getOrdersByCasefile,
    getOrdersByEvidence,
    
    // Metadata
    filterKey,
    isReady: !authLoading && !!userId
  };
};
